import { CartStorage } from "@/libs/cart-storage";
import { envConfig } from "@/libs/env-config";
import {
  ICartData,
  IAddToCartPayload,
  IChangeCartPayload,
  IClearCartPayload,
  IUpdateCartPayload,
} from "@/types/cart";
import { getCommonHeaders, handleApiError } from "./common";

const CART_API_URL = envConfig.apiCartUrl;

// API Endpoint
const CART = "/cart";

const getCartHeaders = () => {
  const headers = getCommonHeaders();

  const token = CartStorage.getCartToken();

  if (token) {
    headers.append("X-Cart-Token", token);
  }

  return headers;
};

/**
 * Fetches the current cart data
 * @returns The cart data
 */
export const getCartApi = async (): Promise<ICartData> => {
  try {
    // Use direct fetch with the cart API URL
    const url = new URL(`${CART_API_URL}${CART}`);

    const response = await fetch(url.toString(), {
      headers: getCartHeaders(),
    });

    const data = await response.json();

    // Save token from response to both cookie and localStorage
    if (data && data.token) {
      CartStorage.saveCartToken(data.token);
    }

    return data;
  } catch (error) {
    return handleApiError(error, "getCart");
  }
};

/**
 * Adds an item to the cart
 * @param payload - The payload containing the items to add
 * @returns The updated cart data
 */
export const addToCartApi = async (
  payload: IAddToCartPayload
): Promise<ICartData> => {
  try {
    // Create URL with token if available
    const url = new URL(`${CART_API_URL}${CART}/add`);

    // Use direct fetch with the cart API URL
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: getCartHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Save token from response to both cookie and localStorage
    if (data && data.token) {
      CartStorage.saveCartToken(data.token);
    }

    return data;
  } catch (error) {
    return handleApiError(error, "addToCart");
  }
};

/**
 * Changes the quantity of an item in the cart
 * @param payload - The payload containing the variant ID and quantity
 * @returns The updated cart data
 */
export const changeCartApi = async (
  payload: IChangeCartPayload
): Promise<ICartData> => {
  try {
    // Create URL with token if available
    const url = new URL(`${CART_API_URL}${CART}/change`);

    // Use direct fetch with the cart API URL
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: getCartHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Save token from response to both cookie and localStorage
    if (data && data.token) {
      CartStorage.saveCartToken(data.token);
    }

    return data;
  } catch (error) {
    return handleApiError(error, "changeCart");
  }
};

/**
 * Clears all items from the cart
 * @param payload - Optional payload for clearing the cart
 * @returns The updated cart data
 */
export const clearCartApi = async (
  payload: IClearCartPayload = {}
): Promise<ICartData> => {
  try {
    // Create URL with token if available
    const url = new URL(`${CART_API_URL}${CART}/clear`);

    // Use direct fetch with the cart API URL
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: getCartHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Save token from response to both cookie and localStorage
    if (data && data.token) {
      CartStorage.saveCartToken(data.token);
    }

    return data;
  } catch (error) {
    return handleApiError(error, "clearCart");
  }
};

/**
 * Updates the cart with new quantities, attributes, or notes
 * @param payload - The payload containing updates to apply
 * @returns The updated cart data
 */
export const updateCartApi = async (
  payload: IUpdateCartPayload
): Promise<ICartData> => {
  try {
    // Create URL with token if available
    const url = new URL(`${CART_API_URL}${CART}/update`);

    // Use direct fetch with the cart API URL
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: getCartHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Save token from response to both cookie and localStorage
    if (data && data.token) {
      CartStorage.saveCartToken(data.token);
    }

    return data;
  } catch (error) {
    return handleApiError(error, "updateCart");
  }
};
